<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import {capitalize, ref} from "vue";
import {Inertia} from "@inertiajs/inertia";
import {Link} from '@inertiajs/inertia-vue3';

const props = defineProps({
  event: Object
});

const loading = ref(false);

const formatStartDate = () => {
  return dayjs(props.event.start_date).format('MMMM DD, YYYY');
}
const formatEndDate = () => {
  const endDate = dayjs(props.event.end_date);

  if (duration() > 1) {
    return `${endDate.format('MMMM DD, YYYY')}`;
  }

  return null;
}

const duration = () => {
  const startDate = dayjs(props.event.start_date);
  const endDate = dayjs(props.event.end_date);

  return endDate.diff(startDate, 'day') + 1;
}

const typeLabel = () => {
  if (props.event.type === 'pto') {
    return 'PTO';
  }

  return capitalize(props.event.type);
}

const statusClasses = () => {
  if (props.event.status === 'approved') {
    return ['bg-green-100', 'text-green-800'];
  } else if (props.event.status === 'declined') {
    return ['bg-red-100', 'text-red-800'];
  }
  return ['bg-orange-100', 'text-orange-800'];
}

const statusLabel = () => {
  return capitalize(props.event.status.replace('_', ' '));
}

const isPtoEvent = () => {
  return props.event.type === 'pto';
}

const approvedOrDeclinedAt = () => {
  if (props.event.approved_at) {
    return dayjs(props.event.approved_at).format('MMMM D, YYYY hh:mm A');
  }
  if (props.event.declined_at) {
    return dayjs(props.event.declined_at).format('MMMM D, YYYY hh:mm A');
  }

  return null;
}

const approveEvent = () => {
  loading.value = true;
  axios.post(route('admin.office-calendar.approve', {calendar_event: props.event.id}))
    .then(() => {
      Inertia.get(route(route().current(), {calendar_event: props.event.id}), {}, {preserveScroll: true});
      loading.value = false;
    }).catch((er) => {
    console.log(er);
    loading.value = false;
  });
}

const declineEvent = () => {
  loading.value = true;
  axios.post(route('admin.office-calendar.decline', {calendar_event: props.event.id}))
    .then(() => {
      Inertia.get(route(route().current(), {calendar_event: props.event.id}), {}, {preserveScroll: true});
      loading.value = false;
    }).catch((er) => {
    console.log(er);
    loading.value = false;
  });
}
</script>

<template>
  <AppLayout title="Calendar Event">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <Link :href="route('admin.office-calendar.index')">
          <SecondaryButton class="mr-2">Back</SecondaryButton>
        </Link>
        Calendar Event
      </h2>
    </template>

    <div class="max-w-screen-xl px-4 py-8 mx-auto lg:px-6 sm:py-16 lg:py-24">
      <div class="max-w-3xl mx-auto space-y-4 text-center">
        <h2 class="text-4xl font-extrabold leading-tight tracking-tight text-gray-900" v-text="event.title"></h2>
        <p class="text-xl font-medium leading-tight text-gray-700">
          <span v-text="formatStartDate()"></span>
          <span v-if="formatEndDate()" v-text="` - ${formatEndDate()}`"></span><br>
          <span class="text-gray-500" v-text="` ${duration()} day(s)`"></span>
        </p>
        <span
          class="bg-primary-100 text-primary-800 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded">
          <span class="font-bold" v-text="typeLabel()"></span>
          &nbsp;by&nbsp;
          <span class="font-bold" v-text="event.user.full_name"></span>
        </span>
        <p class="text-lg font-medium leading-tight text-gray-700">
          <span v-if="event.description" v-html="event.description"></span>
          <span v-else>No description provided</span>
        </p>
        <span
          class="text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded"
          :class="statusClasses()">
          <span class="font-bold" v-text="statusLabel()"></span>
          <span v-if="(event.approved_at || event.declined_at)">
            &nbsp;on&nbsp;<span class="font-bold" v-text="approvedOrDeclinedAt()"></span>
          </span>
        </span>
        <p class="text-lg font-medium leading-tight text-gray-700" v-if="isPtoEvent()">
          <PrimaryButton class="mr-2" @click="approveEvent()" :disabled="loading">Approve</PrimaryButton>
          <SecondaryButton @click="declineEvent()" :disabled="loading">Decline</SecondaryButton>
        </p>
      </div>
    </div>
  </AppLayout>
</template>
